<template>
  <div>
    <h5 class=" mb-2" style="color: #0055A6; font-weight: bolder;">{{ $t("appGroupManagement") }}</h5>
  </div>
  <div class="d-flex justify-content-end gap-3 mb-3">
    <Button v-if="!templatePolicy" @click="onImport" class="custom-icon-button">
      <i class="ag-icon ag-icon_import"></i>
      <span class="label-text">{{ $t("import") }}</span>
    </Button>
  <AppGroupImportModal ref="importModal" @imported="getList" />


    <Button @click="onAddNew" class="custom-icon-button">
      <i class="ag-icon ag-icon_add_icon"></i>
      <span class="label-text">{{ $t("addNewAppgroup") }}</span>
    </Button>
  </div>

  <div class="div-wrapper">
    <div class="input-class d-flex align-items-center gap-2 mt-3 mb-3 px-2 ">
      <div class="search-wrapper position-relative">
        <InputText v-model="searchQuery" placeholder="Search" />
        <i class="pi pi-search search-icon" />
      </div>

      <div class="d-flex align-items-center gap-1">
        <button class="btn btn-link p-0" @click="clearSearch" style="text-decoration: none;">
          <i style="color: #5d5f62;" class="pi pi-times-circle"></i>
          <strong class="clear-text">{{ $t("clear") }}</strong>
        </button>
      </div>

      <span class="mx-1 gray-color">|</span>

      <span class="search-text">{{ $t("filterBy") }}</span>
      <MultiSelect v-model="selectedDiscoveryMode" :options="realOptions" optionLabel="label" optionValue="value"
        placeholder="Discovery Mode" :maxSelectedLabels="3" />

    </div>

    <ag-card
      :module="{ ...module, component: { ...module.component, options: { ...module.component.options, rows: filteredAppGroupsRows } } }"
      @deleteClick="deleteAppGroup"></ag-card>
    <EditPanel :show-enabled="true" v-model="showPanel" :title="formTitle" :form-component="formFileName" :form="formData"
      @save="saveHandler" @cancel="cancelHandler" form-type="appGroup" />
    <ag-loading v-if="loading" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import AppManagement from "@/helpers/pages/AppManagement";
import AgCard from "@/components/ag-card/index.vue";
import EditPanel from '@/components/ag-edit-panel/index.vue'
import AppGroupForm from '@/components/ag-edit-panel/forms/app-group-form.vue'
import Swal from 'sweetalert2';
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import { useStore } from "@/store";
import { useRoute, useRouter } from "vue-router";
import AgLoading from "@/components/ag-loading/index.vue";
import AppGroupImportModal from "@/components/ag-edit-panel/forms/app-group-import-modal.vue";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const loading = ref(false);
const store = useStore();
const router = useRouter();

const searchQuery = ref('')
const selectedDiscoveryMode = ref('');
const showPanel = ref(false)
let formTitle = ref(t('addNewAppGroup'))
let formFileName = ref(AppGroupForm)
const formData = ref({
  id: 0,
  name: "",
  description: "",
  memorySecurity: 0,
  inheritance: 0,
  subsystemsInDiscoveryMode: 0,
  audit: 1,
  companyId: ""
})
const discoveryOptions = computed(() => store.state.general.enums['DiscoveryMode'] || [])
const realOptions = computed(() => discoveryOptions.value.filter(opt => opt.label !== 'Enable All'))
const company = computed(() => store.getters.company);
const templatePolicy = ref(false);
const group = ref(null);
const site = ref({});
const template =ref('');
const templateId = ref(0);
const currentRole = ref('');

function clearSearch() {
  searchQuery.value = '';
  selectedDiscoveryMode.value = '';
}

function updateRowsFromStore() {
  if (module.value?.component?.options) {
    module.value.component.options.rows = appGroupList.value;
  }
}

let module = ref(AppManagement);

const appGroupList = computed(() => store.state.general.appGroups);

watch(appGroupList, () => {
  updateRowsFromStore();
});
const getList = async () => {
  try {
    await store.dispatch(GeneralActionTypes.GET_APPGROUPS);
  } finally {
    module.value.component.options.isLoading = false;
  }
};
const getListTemplate = async () => {
  try {
    await store.dispatch(GeneralActionTypes.GET_APPGROUPS_TEMPLATE);
  } finally {
    module.value.component.options.isLoading = false;
  }
};
const fetchEnumAndStore = async (name) => {
  await store.dispatch(GeneralActionTypes.GET_ENUM_BY_NAME, name);
};
const filteredAppGroupsRows = computed(() => {
  const query = searchQuery.value.toLowerCase();
  const selectedModes = selectedDiscoveryMode.value;
  const rows = module.value.component.options.rows || [];

  return rows.filter(row => {
    const matchesSearch = !row.name || row.name.toLowerCase().includes(query);

    const matchesAppList = row.appGroupProcess?.some(process =>
      process.fileName?.toLowerCase().includes(query)
    );

    const matchesDiscoveryMode =
      !selectedModes || selectedModes.length === 0 || selectedModes.some(mode =>
        (row.subsystemsInDiscoveryMode & mode) === mode
      );

    return (matchesSearch || matchesAppList) && matchesDiscoveryMode;
  });
})
const getFolderMaps = async () => {
  await store.dispatch(GeneralActionTypes.GET_FOLDER_MAPS);
}

onMounted(() => {
  getFolderMaps();
  const routeName = useRoute().name;
  if (routeName == 'templateAppGroupManagement') {
    const templateStr = localStorage.getItem("agms_template");
    const mainId = templateStr ? JSON.parse(templateStr)?.mainid : null;
    templateId.value = mainId;
    currentRole.value = templateStr ? JSON.parse(templateStr)?.template : null;
    templatePolicy.value = true;
    getListTemplate();
  } else {
    getList();
  }
  group.value = JSON.parse(localStorage.getItem("agms_group")) || {};
  site.value = JSON.parse(localStorage.getItem("agms_site")) || {};
  updateRowsFromStore();
  fetchEnumAndStore("Inheritance");
  fetchEnumAndStore("MemorySecurity");
  fetchEnumAndStore("DiscoveryMode");
  fetchEnumAndStore("AppGroupAudit");
  fetchEnumAndStore("AppGroupType");

});
const importModal = ref(null);
const onImport = () => {
  
   if (importModal.value?.open) {
     importModal.value.open();
   } else {
     console.error("Modal ref not found!");
   }
};


const onAddNew = () => {
  formTitle.value = t('addNewAppGroup');
  formFileName.value = AppGroupForm;
  showPanel.value = true
};

const extractErrorMessage = (errors) => {
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.flat().join("<br>");
  if (typeof errors === "object") return Object.values(errors).flat().join("<br>");
  return String(errors);
};

const saveHandler = async (dataFromForm) => {
  const group = localStorage.getItem("group");
  const groupId = group ? JSON.parse(group)?.mainid : null;
  let body = {
    id: 0,
    name: dataFromForm.name,
    description: dataFromForm.description,
    memorySecurity: dataFromForm.memorySecurity,
    inheritance: dataFromForm.inheritance,
    subsystemsInDiscoveryMode: dataFromForm.subsystemsInDiscoveryMode,
    audit: dataFromForm.audit,
    companyId: company.value.companyGuid,
    groupId: templatePolicy.value ? null : groupId,
    enabled: dataFromForm.enabled ? dataFromForm.enabled : false,
    appGroupType: dataFromForm.appGroupType,
    expertNotes: dataFromForm.expertNotes,
    childProcess: dataFromForm.childProcess,
    templateId: templatePolicy.value ? templateId.value: null,
  }
  try {
    const response = templatePolicy.value ? await store.dispatch(
      GeneralActionTypes.CREATE_OR_UPDATE_APPGROUP_TEMPLATE,
      body
    ) : await store.dispatch(
      GeneralActionTypes.CREATE_OR_UPDATE_APPGROUP,
      body
    );
    loading.value = true;
    if (response.isSuccess) {
      loading.value = false;
    }
    const appGroupId = response.entityData;
    if(templatePolicy.value){
      getListTemplate();
    }else{
      getList();
    }
    showPanel.value = false;
    router.push({
      path: templatePolicy.value ? `/administration/app-group-management/detail-role` : `/administration/app-group-management/detail`,
      query: { id: appGroupId }
    });
  } catch (err) {
    const serverData = err?.response?.data ?? err?.data ?? null;
    let msg = "";

    if (serverData?.errors) {
      msg = extractErrorMessage(serverData.errors);
    } else if (serverData?.title) {
      msg = serverData.title;
    } else if (err?.message) {
      msg = err.message;
    } else {
      msg = JSON.stringify(err);
    }

    return Swal.fire({ title: "Error", html: msg || "Unexpected error", icon: "error" });
  }
}

async function deleteAppGroup(rowData) {
  const result = await Swal.fire({
    title: "Are You Sure?",
    icon: "warning",
    text: "You are about to delete the selected item!",
    showCancelButton: true,
    confirmButtonText: "Delete",
    cancelButtonText: "Cancel",
  });
  if (result.isConfirmed) {
    try {
      const data = templatePolicy.value ? { ...rowData, templateId: templateId.value } : rowData.id;
      const response = templatePolicy.value 
        ? await store.dispatch(GeneralActionTypes.DELETE_APPGROUP_TEMPLATE, data) 
        : await store.dispatch(GeneralActionTypes.DELETE_APPGROUP, data);
      if (response.isSuccess) {
        Swal.fire("Deleted!", "App group deleted successfully.", "success");
        if(templatePolicy.value){
          getListTemplate();
        }else{
          getList();
        }
      } else {
        Swal.fire("Error", response.data?.message || "Delete failed.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong while deleting.", "error");
    }
  }
};
</script>

<style scoped>
.div-wrapper {
  border-top: 1px solid #D2D9E2;
  border-left: 1px solid #D2D9E2;
  border-right: 1px solid #D2D9E2;
}

.custom-icon-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: #2c5f89;
  font-weight: bold;
  text-transform: uppercase;
  padding: 0;
  font-size: 0.8rem;
}

.div-wrapper #ag-table .ag-icon {
  font-size: 1.1em !important;
}

.custom-icon-button:hover {
  opacity: 0.8;
  cursor: pointer;
}

.icon-circle {
  background-color: #2c5f89;
  color: white;
  border-radius: 50%;
  padding: 8px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  padding-left: 5px;
  border-left: 1px solid #D2D9E2;
}

.p-inputtext {
  padding: 0.25rem 0.25rem;
  font-size: 0.875rem;
  background: transparent;
}

::v-deep(.p-multiselect .p-multiselect-label) {
  font-size: 0.875rem;
  padding: 0.25rem 0.25rem !important;
}

.p-multiselect {
  background: transparent !important;
}

.clear-text {
  color: #5d5f62;
  font-size: 0.875rem;
  margin-left: 5px;
}

.search-text {
  color: #5d5f62;
  font-size: 0.875rem;
}

.label-text {
  color: #3C73AA;
}
</style>
